import React, { Fragment, FunctionComponent, useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { StepperContent, StepperHeader, StepperSubmit } from '../../../../../shared/general';
import { utilsAdditionalPaymentGetDefaultValuesStep1 } from '../../utils';
import { FormElementDynamicInputTyped } from '../../../../../shared/form-elements';
import {
  CalculatorAdditionalPaymentFormFields,
  CalculatorAdditionalPaymentApiResponse,
  CalculatorAdditionalPaymentStep1FormFields
} from '../../types';
import {
  utilNumberToCurrency,
  utilStringToNumber,
  utilsValidationsDateMustBeAfter
} from '../../../../../../utils';
import { useFormContext } from '../../../../../../hooks';
import { useIntl } from 'react-intl';
import { useAdditionalPaymentStep1FormFields } from '../../config';
import { validationMessagesTranslated } from 'src/components/shared/validation';

export const CalculatorAdditionalPaymentStepperStep1: FunctionComponent<{
  id: number;
  title?: string;
}> = ({ id, title }) => {
  const intl = useIntl();
  const AdditionalPaymentStep1FormFields = useAdditionalPaymentStep1FormFields(intl);

  const [completedText, setCompletedText] = useState('');
  const { updateForm, setError } = useFormContext<
    CalculatorAdditionalPaymentApiResponse,
    CalculatorAdditionalPaymentFormFields
  >();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid }
  } = useForm({
    mode: 'onBlur',
    defaultValues: utilsAdditionalPaymentGetDefaultValuesStep1(intl)
  });

  const mortgageType = watch('mortgageType');
  const showAdditionFormFields = useMemo(
    () => ['annafl', 'linafl'].includes(mortgageType),
    [mortgageType]
  );

  const onSubmit = useCallback(
    async (formData: Record<keyof CalculatorAdditionalPaymentStep1FormFields, string>) => {
      try {
        updateForm(formData);
        const { mortgageAmount, mortgageAmount2, mortgageType } = formData;
        const addMortgageAmount2 =
          mortgageType === 'annafl' || mortgageType === 'linafl'
            ? utilStringToNumber(mortgageAmount2)
            : 0;
        setCompletedText(
          utilNumberToCurrency(
            utilStringToNumber(mortgageAmount) + addMortgageAmount2,
            true,
            0,
            intl.locale
          )
        );
      } catch (e) {
        setError(true);
      }
    },
    [updateForm, setCompletedText, setError]
  );

  const getLabelMortgageAmount = useCallback(() => {
    if (mortgageType === 'linafl') {
      return intl.formatMessage({
        id: 'step1.section.mortgageAmount.lineair',
        defaultMessage: 'Lineair hypotheekdeel'
      });
    }
    if (mortgageType === 'annafl') {
      return intl.formatMessage({
        id: 'step1.section.mortgageAmount.annuiteiten',
        defaultMessage: 'Annuïteiten hypotheekdeel'
      });
    }
    return intl.formatMessage({
      id: 'step1.section.mortgageAmount',
      defaultMessage: 'Hypotheek'
    });
  }, [mortgageType, intl]);

  return (
    <Fragment>
      <StepperHeader
        onClose={handleSubmit(onSubmit)}
        title={title}
        id={id}
        completedText={completedText}
      />
      <StepperContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormElementDynamicInputTyped<CalculatorAdditionalPaymentStep1FormFields>
            formFields={AdditionalPaymentStep1FormFields}
            id={'mortgageType'}
            register={register}
            variant={`calculator`}
            errors={errors}
            values={watch()}
            options={{ required: true }}
          />

          <FormElementDynamicInputTyped<CalculatorAdditionalPaymentStep1FormFields>
            formFields={AdditionalPaymentStep1FormFields}
            id={'remainingTerm'}
            register={register}
            validation={{
              required: validationMessagesTranslated(intl).required,
              validate: (value: string) =>
                utilsValidationsDateMustBeAfter(
                  value,
                  validationMessagesTranslated(intl).dateMustBeInTheFuture
                )
            }}
            errors={errors}
            values={watch()}
          />

          <FormElementDynamicInputTyped<CalculatorAdditionalPaymentStep1FormFields>
            formFields={AdditionalPaymentStep1FormFields}
            id={'mortgageAmount'}
            register={register}
            errors={errors}
            values={watch()}
            variant={'calculator'}
            options={{
              required: true,
              label: getLabelMortgageAmount()
            }}
          />
          <FormElementDynamicInputTyped<CalculatorAdditionalPaymentStep1FormFields>
            formFields={AdditionalPaymentStep1FormFields}
            id={'mortgageInterestRate'}
            register={register}
            errors={errors}
            values={watch()}
            options={{ required: true }}
          />

          {showAdditionFormFields && (
            <>
              <FormElementDynamicInputTyped<CalculatorAdditionalPaymentStep1FormFields>
                formFields={AdditionalPaymentStep1FormFields}
                id={'mortgageAmount2'}
                register={register}
                errors={errors}
                variant={'calculator'}
                values={watch()}
                options={{
                  required: true,
                  label: intl.formatMessage({
                    id: 'step1.section.mortgageAmount2',
                    defaultMessage: 'Aflossingsvrij hypotheekdeel'
                  })
                }}
              />

              <FormElementDynamicInputTyped<CalculatorAdditionalPaymentStep1FormFields>
                formFields={AdditionalPaymentStep1FormFields}
                id={'mortgageInterestRate2'}
                register={register}
                errors={errors}
                values={watch()}
                options={{ required: true }}
              />
            </>
          )}

          <StepperSubmit id={id} isValidated={isValid} />
        </form>
      </StepperContent>
    </Fragment>
  );
};
